/**
 * Display-only mirror of the MCP tool catalog (infra/lambda/mcp-server/tools.ts)
 * — this powers the "Outils disponibles" list in McpIntegration.tsx so a
 * customer can see what Claude can do before generating a token, with one
 * example of how they'd actually ask for it in a Claude conversation.
 *
 * Deliberately NOT imported from the Lambda: it's a separate deployable
 * (see docs/mcp-server.md's "no NestJS import, no shared DI graph"
 * decision) with its own build, so there's no module boundary that could
 * import this file either way. Kept in sync by hand — if a tool is added,
 * renamed, or its readOnly flag changes in tools.ts, mirror it here too.
 * exampleKey/exampleKeyWrite point at i18n keys (mcpTokens.tools.<name>.example)
 * so the example sentence localizes with the rest of the page.
 */
export interface McpToolCatalogEntry {
  name: string;
  readOnly: boolean;
  descriptionKey: string;
  exampleKey: string;
  conversationsOnly?: boolean;
}

export const MCP_TOOL_CATALOG: McpToolCatalogEntry[] = [
  {
    name: 'list_brands',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.list_brands.desc',
    exampleKey: 'mcpTokens.tools.list_brands.example',
  },
  {
    name: 'get_brand_overview',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.get_brand_overview.desc',
    exampleKey: 'mcpTokens.tools.get_brand_overview.example',
  },
  {
    name: 'get_audit_summary',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.get_audit_summary.desc',
    exampleKey: 'mcpTokens.tools.get_audit_summary.example',
  },
  {
    name: 'get_competitor_matrix',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.get_competitor_matrix.desc',
    exampleKey: 'mcpTokens.tools.get_competitor_matrix.example',
  },
  {
    name: 'get_engine_influence',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.get_engine_influence.desc',
    exampleKey: 'mcpTokens.tools.get_engine_influence.example',
  },
  {
    name: 'list_tasks',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.list_tasks.desc',
    exampleKey: 'mcpTokens.tools.list_tasks.example',
  },
  {
    name: 'get_credit_usage',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.get_credit_usage.desc',
    exampleKey: 'mcpTokens.tools.get_credit_usage.example',
  },
  {
    name: 'create_task',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.create_task.desc',
    exampleKey: 'mcpTokens.tools.create_task.example',
  },
  {
    name: 'update_task_status',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.update_task_status.desc',
    exampleKey: 'mcpTokens.tools.update_task_status.example',
  },
  {
    name: 'trigger_audit',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.trigger_audit.desc',
    exampleKey: 'mcpTokens.tools.trigger_audit.example',
  },
  {
    name: 'add_competitor',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.add_competitor.desc',
    exampleKey: 'mcpTokens.tools.add_competitor.example',
  },
  {
    name: 'add_brand_query',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.add_brand_query.desc',
    exampleKey: 'mcpTokens.tools.add_brand_query.example',
  },
  {
    name: 'list_targets',
    readOnly: true,
    descriptionKey: 'mcpTokens.tools.list_targets.desc',
    exampleKey: 'mcpTokens.tools.list_targets.example',
  },
  {
    name: 'upsert_target',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.upsert_target.desc',
    exampleKey: 'mcpTokens.tools.upsert_target.example',
  },
  {
    name: 'delete_target',
    readOnly: false,
    descriptionKey: 'mcpTokens.tools.delete_target.desc',
    exampleKey: 'mcpTokens.tools.delete_target.example',
  },
  {
    name: 'list_conversation_personas',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.list_conversation_personas.desc',
    exampleKey: 'mcpTokens.tools.list_conversation_personas.example',
  },
  {
    name: 'get_conversation_persona',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.get_conversation_persona.desc',
    exampleKey: 'mcpTokens.tools.get_conversation_persona.example',
  },
  {
    name: 'get_conversation_rollup',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.get_conversation_rollup.desc',
    exampleKey: 'mcpTokens.tools.get_conversation_rollup.example',
  },
  {
    name: 'list_conversation_launches',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.list_conversation_launches.desc',
    exampleKey: 'mcpTokens.tools.list_conversation_launches.example',
  },
  {
    name: 'get_conversation_launch',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.get_conversation_launch.desc',
    exampleKey: 'mcpTokens.tools.get_conversation_launch.example',
  },
  {
    name: 'get_conversation',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.get_conversation.desc',
    exampleKey: 'mcpTokens.tools.get_conversation.example',
  },
  {
    name: 'create_conversation_persona',
    readOnly: false,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.create_conversation_persona.desc',
    exampleKey: 'mcpTokens.tools.create_conversation_persona.example',
  },
  {
    name: 'trigger_conversation_run',
    readOnly: false,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.trigger_conversation_run.desc',
    exampleKey: 'mcpTokens.tools.trigger_conversation_run.example',
  },
  // Brand Persona tools (2026-10-11) — the reusable « Qui est-il ? » cards, separate
  // from the conversation tools above.
  {
    name: 'list_personas',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.list_personas.desc',
    exampleKey: 'mcpTokens.tools.list_personas.example',
  },
  {
    name: 'get_persona',
    readOnly: true,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.get_persona.desc',
    exampleKey: 'mcpTokens.tools.get_persona.example',
  },
  {
    name: 'create_persona',
    readOnly: false,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.create_persona.desc',
    exampleKey: 'mcpTokens.tools.create_persona.example',
  },
  {
    name: 'update_persona',
    readOnly: false,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.update_persona.desc',
    exampleKey: 'mcpTokens.tools.update_persona.example',
  },
  {
    name: 'delete_persona',
    readOnly: false,
    conversationsOnly: true,
    descriptionKey: 'mcpTokens.tools.delete_persona.desc',
    exampleKey: 'mcpTokens.tools.delete_persona.example',
  },
];
